/**
 * Zentrale Registry der Kunden-Portraits — authentische Snapshot-Ästhetik
 * (lokale Assets unter /avatars/authentic/ · jede Person einzigartig,
 * KEINE Wiederholungen plattformweit).
 *
 * FACES     — quadratische Headshots (Business-Testimonials)
 * PORTRAITS — hochformatige Editorial-Portraits (DriverStories)
 */

const A = (slug) => `/avatars/authentic/${slug}.webp`;

// Liefert die passende Thumbnail-Variante (96px/224px) eines Authentic-Portraits — spart Ladezeit bei kleiner Darstellung.
export const avatarThumb = (src, size = 96) => {
  if (!src || !src.startsWith('/avatars/authentic/')) return src;
  const base = src.slice('/avatars/authentic/'.length).replace(/\.(webp|jpe?g|png)$/i, '');
  if (base.includes('/')) return src;
  return `/avatars/authentic/${size}/${base}.webp`;
};

export const FACES = {
  // Business-Testimonials (nur Testimonials.jsx)
  bizSandra:  A('t-sandra'),   // 43 · Flottenmanagerin
  bizPeter:   A('t-peter'),    // 58 · Geschäftsführer
  bizDaniela: A('t-daniela'),  // 47 · CFO
  bizStefan:  A('t-stefan'),   // 49 · Inhaber
  bizMiriam:  A('t-miriam'),   // 38 · Vertriebsleiterin
  bizJan:     A('t-jan'),      // 36 · Fuhrparkleiter
  // Service-Seiten-Kundenstimmen — authentische Smartphone-Schnappschüsse (Aug 2026, Stil wie Warenkorb-Reviews)
  tobias:    A('s2-tobias'),    // 38 · Stuttgart · Reifen
  lisa:      A('s2-lisa'),      // 34 · Bremen · Reifen
  andreas:   A('uwe-47-augsburg'),     // Uwe B. · 47 · Augsburg · Reifen (User-Upload)
  daniel:    A('s2-daniel'),    // 42 · Berlin · Öl + Autokredit
  miriam:    A('s2-miriam'),    // 37 · Hannover · Öl
  stefan:    A('jonas-28-berlin'),     // Jonas F. · 28 · Berlin · Öl (User-Upload)
  thomas:    A('s2-thomas'),    // 48 · Köln · Kfz-Steuer
  sarah:     A('s2-sarah'),     // 31 · Dresden · Kfz-Steuer + Autokredit
  julian:    A('s2-julian'),    // 35 · Frankfurt · Kfz-Steuer
  jan:       A('s2-jan'),       // 39 · Leipzig · Autokredit + Werkstatt
  oliver:    A('torsten-48-dortmund'), // Torsten M. · 48 · Dortmund · Werkstatt (User-Upload)
  daniela:   A('bjoern-42-winsen'),    // Björn T. · 42 · Winsen (Luhe) · Werkstatt (User-Upload)
  matthias:  A('s2-matthias'),  // 43 · Köln · TÜV + Versicherung
  julia:     A('franzi-32-muenchen'),  // Franzi M. · 32 · München · TÜV (User-Upload)
  peter:     A('s2-peter'),     // 61 · Nürnberg · TÜV
  katharina: A('s2-katharina'), // 36 · Düsseldorf · Versicherung
  sandra:    A('s2-sandra'),    // 40 · Hamburg · Versicherung
  // NUR Auto-Verkauf (SellCarPage) — einzigartig, NICHT auf anderen Seiten wiederverwenden!
  holger:    '/avatars/sellcar-holger.webp', // 52 · Stuttgart
  sabine:    '/avatars/sellcar-sabine.webp', // 41 · Bochum
  svenja:    '/avatars/sellcar-svenja.webp', // 29 · Münster
};

// Candid-Reisefotos für die Luxushotel-Kundenstories (LuxushotelsPage)
export const TRAVEL_PORTRAITS = {
  julian:    A('p-travel-julian'),    // 34 · Beverly Hills LA
  katharina: A('p-travel-katharina'), // 36 · Copacabana Rio
  tobias:    A('p-travel-tobias'),    // 39 · Manhattan NY
};

export const PORTRAITS = {
  thomas:  A('p-thomas'),  // 47 · Außendienst · Köln
  markus:  A('p-markus'),  // 49 · Gründer · Stuttgart
  sandra:  A('p-sandra'),  // 41 · Designerin · Hamburg
  claudia: A('p-claudia'), // 38 · Projekt-Managerin · München
};

export const CUSTOMER_AVATARS = [
  { id: 'clemens-55-hamburg',    alt: 'Niklas W., FuelX-Kunde aus Bochum' },
  { id: 'birgit-49-hannover',    alt: 'Jörg B., FuelX-Kunde aus Karlsruhe' },
  { id: 'silke-51-magdeburg',    alt: 'Sarah L., FuelX-Kundin aus Hannover' },
  { id: 'reinhold-59-wiesbaden', alt: 'Sven K., FuelX-Kunde aus Flensburg' },
  { id: 'daniel-44-mannheim',    alt: 'Luca B., FuelX-Kunde aus Heidelberg' },
];

// Authentische Avatare (Snapshot-Ästhetik, Juni 2026) — ein Bild pro Person, plattformweit einzigartig.
const REVIEW_IDS = [
  'silke-51-magdeburg', 'birgit-49-hannover', 'aynur-42-nuernberg', 'malte-45-kiel',
  'clemens-55-hamburg', 'franziska-31-leipzig', 'detlef-63-bremen', 'yusuf-38-koeln',
  'philipp-27-augsburg', 'renate-57-dortmund', 'jasmin-33-essen', 'reinhold-59-wiesbaden',
  'carolin-40-mainz', 'erik-29-rostock', 'gudrun-66-freiburg', 'kevin-24-magdeburg',
  'stefan-33-hamburg', 'julian-41-frankfurt', 'markus-48-stuttgart', 'matthias-54-koeln',
  'thomas-47-koeln', 'andreas-52-augsburg', 'peter-46-nuernberg', 'sabine-52-muenchen',
  'jens-46-berlin', 'nadja-39-leipzig', 'oliver-51-dresden',
];
const NEW_AVATARS = Object.fromEntries(REVIEW_IDS.map((id) => [id, A(id)]));

// User-Upload-Schnappschüsse (Aug 2026) — ersetzen die alten Porträts dieser Personen plattformweit.
Object.assign(NEW_AVATARS, {
  'silke-51-magdeburg':    A('sarah-36-hannover'),
  'birgit-49-hannover':    A('joerg-47-karlsruhe-v2'),
  'clemens-55-hamburg':    A('niklas-29-bochum'),
  'daniel-44-mannheim':    A('luca-27-heidelberg'),
  'anke-47-luebeck':       A('anke-45-luebeck-v4'),
  'reinhold-59-wiesbaden': A('sven-43-flensburg-v3'),
  // Aug 24, 2026: neue Portraits — Cache-Bust durch v2-Slug
  'elias-32-berlin':       A('lena-34-berlin'),
  'peter-46-nuernberg':    A('peter-46-nuernberg-v2'),
  'bernd-39-hamburg':      A('bernd-39-hamburg-v2'),
  'renate-57-dortmund':    A('renate-57-dortmund-v2'),
});

export const avatarSrc = (id) => NEW_AVATARS[id] || FACES[id];
